import React, { useState, useEffect, useRef } from 'react';
import { getConversations, getUserGroups, getAllUsers, searchMessages } from '../../services/api';
import { hasImpulsaAccess } from '../../utils/access';
import { getSocket } from '../../services/socket';
import CreateGroupModal from '../CreateGroupModal/CreateGroupModal';
import ProfileModal from '../ProfileModal/ProfileModal';
// V1 channels deprecated — import mantenido por si se necesita rollback
// import ChannelsModal from '../Channels/ChannelsModal';
import ChannelsV2Modal from '../ChannelsV2/ChannelsV2Modal';
import ChannelProfile from '../ChannelsV2/ChannelProfile';
import SettingsModal from '../SettingsModal/SettingsModal';
import ContactExchangeModal from '../ContactExchange/ContactExchangeModal';
import CRMModal from '../CRM/CRMModal';
import CRMActivitySidebar from '../CRM/CRMActivitySidebar';
import ImageViewer from '../ImageViewer/ImageViewer';
import ui from '../../i18n/ui';
import { Icons } from '../Icons/Icons';
import t from '../../i18n/useTranslation';
import impulsaCt from '../Impulsa/i18n';
import AdminPanel from '../AdminPanel/AdminPanel';
import KaixoDirect from '../KaixoDirect/KaixoDirect';
import TranslateCameraModal from '../TranslateCameraModal/TranslateCameraModal';
import ReactionGame from '../ReactionGame/ReactionGame';
import PhoneContactsView from '../PhoneContactsView/PhoneContactsView';
// formatRelativeDate ahora se usa dentro de PhoneContactsView; aquí no.
import kaixoLogo from '../../assets/favicon.png';
import './ConversationList.css';

function ConversationList({
    currentUser,
    onSelectConversation,
    refreshKey,
    onlineUsers,
    // En web, App.js gestiona el panel activo (Latidos / Impulsa) en la
    // columna2. Pasa estas tres props para que ConversationList se comporte
    // como controlled: la tab activa viene de fuera y los embeds no se
    // renderizan dentro de la lista. En móvil estas props son undefined y
    // ConversationList vuelve a su comportamiento clásico (uncontrolled).
    activeTab,
    onTabChange,
    embeddedDisabled,
    // En web cuando el panel activo es Impulsa, mostramos el sidebar de
    // notas del CRM dentro de esta columna1, manteniendo el header con
    // tabs (Chats / Latidos / Impulsa) visible para que el usuario pueda
    // volver. Solo el body de la lista se reemplaza.
    showCrmSidebarInWeb
}) {
    const [conversations, setConversations] = useState([]);
    const [groups, setGroups] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
    const [notification, setNotification] = useState(null);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showHeaderMenu, setShowHeaderMenu] = useState(false);
    const [showChannelsV2Modal, setShowChannelsV2Modal] = useState(false);
    const [internalMainTab, setInternalMainTab] = useState('chats'); // fallback si no controlled
    const mainTab = activeTab !== undefined ? activeTab : internalMainTab;
    const setMainTab = (tab) => {
        if (typeof onTabChange === 'function') onTabChange(tab);
        if (activeTab === undefined) setInternalMainTab(tab);
    };
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showContactExchange, setShowContactExchange] = useState(false);
    const [showAdminSupport, setShowAdminSupport] = useState(false);
    const [showKaixoDirect, setShowKaixoDirect] = useState(false);
    const [showTranslateCamera, setShowTranslateCamera] = useState(false);
    // Una vez abierta la pestaña Latidos, mantenemos el modal montado
    // (oculto con display:none) para que cambios de pestaña posteriores
    // sean instantáneos. Sin esto, cada click a Latidos volvía a llamar
    // a v2GetFeed + v2GetUserChannels y mostraba el spinner de carga.
    const [latidosOpenedOnce, setLatidosOpenedOnce] = useState(false);
    const [showReactionGame, setShowReactionGame] = useState(false);
    const [fullImage, setFullImage] = useState(null);
    const [v2UnreadCount, setV2UnreadCount] = useState(0);
    const [directProfileCode, setDirectProfileCode] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const menuRef = useRef(null);
    const searchTimerRef = useRef(null);

    // Capacitor Contacts: usado para "Añadir a contactos del celular" cuando
    // alguien que no está en tu agenda te escribe.
    const [contactToast, setContactToast] = useState('');
    const handleAddDeviceContact = async (otherUser) => {
        if (!otherUser?.phone) {
            setContactToast('No tenemos su número para añadirlo.');
            setTimeout(() => setContactToast(''), 2200);
            return;
        }
        let ContactsPlugin = null;
        try { ContactsPlugin = require('@capacitor-community/contacts').Contacts; } catch {}
        if (!ContactsPlugin?.createContact) {
            setContactToast('Esta acción solo funciona en la app móvil.');
            setTimeout(() => setContactToast(''), 2200);
            return;
        }
        try {
            await ContactsPlugin.createContact({
                contact: {
                    name: { given: otherUser.name || 'KAIXO' },
                    phones: [{ number: otherUser.phone, isPrimary: true }]
                }
            });
            setContactToast(`✓ ${otherUser.name} añadido a tus contactos`);
            // Re-disparar load del PhoneContactsView (vía evento global)
            try { window.dispatchEvent(new CustomEvent('kaixo_contacts_changed')); } catch {}
            setTimeout(() => setContactToast(''), 2500);
        } catch (e) {
            setContactToast('No se pudo añadir: ' + (e?.message || 'error'));
            setTimeout(() => setContactToast(''), 2500);
        }
    };

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        clearTimeout(searchTimerRef.current);
        if (!value.trim() || value.trim().length < 2) {
            setSearchResults([]);
            return;
        }
        setSearching(true);
        searchTimerRef.current = setTimeout(async () => {
            try {
                const res = await searchMessages(currentUser._id, value.trim());
                setSearchResults(res.data.results || []);
            } catch { setSearchResults([]); }
            finally { setSearching(false); }
        }, 300);
    };

    const handleResultClick = (result) => {
        if (result.groupId) {
            const group = groups.find(g => g._id === result.groupId.toString());
            if (group) onSelectConversation({ type: 'group', id: group._id, name: group.name, members: group.members, admins: group.admins, avatar: group.avatar });
        } else if (result.conversationId) {
            const conv = conversations.find(c => c.id === result.conversationId.toString());
            if (conv) onSelectConversation({ id: conv.id, otherUser: conv.otherUser });
        }
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
    };

    // Caché en memoria de la última lista cruda de usuarios. La usamos
    // para recalcular "others" (kaixo users sin conversación) sin tener
    // que volver a pegarle a /users/all en cada mensaje recibido.
    const allUsersRawRef = useRef([]);
    const lastUsersFetchRef = useRef(0);

    // Refresca SOLO conversaciones + grupos. Es lo que cambia a cada
    // mensaje. La lista de usuarios cambia raramente y se actualiza por
    // separado (al montar y al volver de background). Esto reduce
    // drásticamente las peticiones a /users/all bajo carga.
    const loadConvAndGroups = async () => {
        const [convResR, groupResR] = await Promise.allSettled([
            getConversations(currentUser._id),
            getUserGroups(currentUser._id)
        ]);

        if (groupResR.status === 'fulfilled') {
            setGroups(groupResR.value.data.groups || []);
        } else {
            console.warn('[ConversationList] getUserGroups falló:', groupResR.reason?.message);
        }

        if (convResR.status === 'fulfilled') {
            const convs = convResR.value.data.conversations || [];
            setConversations(convs);
            // Recalcular "others" usando el cache de usuarios — sin pedir red
            const cachedUsers = allUsersRawRef.current;
            if (cachedUsers && cachedUsers.length > 0) {
                const convUserIds = convs.map(c => c.otherUser?.id?.toString()).filter(Boolean);
                const others = cachedUsers.filter(u =>
                    u._id?.toString() !== currentUser._id?.toString() &&
                    !convUserIds.includes(u._id?.toString())
                );
                setAllUsers(others);
            }
        } else {
            console.warn('[ConversationList] getConversations falló:', convResR.reason?.message);
        }

        setLoading(false);
    };

    // Recarga la lista de usuarios desde /users/all. Cara — solo al montar
    // y al volver de background tras >5 min. Después actualiza "others".
    // Lee conversaciones desde conversationsRef (definido más abajo) para
    // evitar closure stale y race con loadConvAndGroups en paralelo.
    const refreshUsers = async () => {
        try {
            const usersRes = await getAllUsers();
            const rawUsers = usersRes.data.users || [];
            allUsersRawRef.current = rawUsers;
            lastUsersFetchRef.current = Date.now();
            const convs = conversationsRef.current || [];
            const convUserIds = convs.map(c => c.otherUser?.id?.toString()).filter(Boolean);
            const others = rawUsers.filter(u =>
                u._id?.toString() !== currentUser._id?.toString() &&
                !convUserIds.includes(u._id?.toString())
            );
            setAllUsers(others);
        } catch (err) {
            console.warn('[ConversationList] getAllUsers falló:', err?.message);
        }
    };

    // loadAll: full reload — usado en montaje inicial y refreshKey externo.
    const loadAll = async () => {
        await Promise.all([loadConvAndGroups(), refreshUsers()]);
    };

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    // Refrescar usuarios al volver de background tras >5 min. Cubre el
    // caso "abro la app después de horas y debe haber usuarios nuevos".
    useEffect(() => {
        const STALE_MS = 5 * 60 * 1000;
        const checkOnFocus = () => {
            if (document.visibilityState !== 'visible') return;
            if (Date.now() - lastUsersFetchRef.current > STALE_MS) {
                refreshUsers();
            }
        };
        document.addEventListener('visibilitychange', checkOnFocus);
        window.addEventListener('focus', checkOnFocus);
        return () => {
            document.removeEventListener('visibilitychange', checkOnFocus);
            window.removeEventListener('focus', checkOnFocus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refs para que los handlers lean siempre el estado actual sin reregistrar el listener
    const conversationsRef = useRef(conversations);
    const allUsersRef = useRef(allUsers);
    const onSelectRef = useRef(onSelectConversation);
    useEffect(() => { conversationsRef.current = conversations; }, [conversations]);
    useEffect(() => { allUsersRef.current = allUsers; }, [allUsers]);
    useEffect(() => { onSelectRef.current = onSelectConversation; }, [onSelectConversation]);

    const groupsRef = useRef(groups);
    useEffect(() => { groupsRef.current = groups; }, [groups]);

    useEffect(() => {
        const openChatWithUser = (e) => {
            const targetId = e.detail?.userId;
            if (!targetId) return;
            const conv = conversationsRef.current.find(c => c.otherUser?.id?.toString() === targetId.toString());
            if (conv) {
                onSelectRef.current({ id: conv.id, otherUser: conv.otherUser });
                return;
            }
            const user = allUsersRef.current.find(u => u._id?.toString() === targetId.toString());
            if (user) {
                onSelectRef.current({ id: null, otherUser: { id: user._id, name: user.name, avatar: user.avatar, language: user.language, region: user.region, status: user.status } });
            }
        };
        // Tap en notificación FCM → abrir conversación por id (1-a-1) o
        // grupo por id. Disparado desde fcmNotifications.js al pulsar la
        // notificación cuando la app estaba cerrada / en background.
        const openChatById = (e) => {
            const { conversationId, groupId } = e.detail || {};
            if (groupId) {
                const g = groupsRef.current.find(x => String(x._id) === String(groupId));
                if (g) {
                    onSelectRef.current({ id: g._id, type: 'group', name: g.name, avatar: g.avatar, members: g.members });
                    return;
                }
            }
            if (conversationId) {
                const c = conversationsRef.current.find(x => String(x.id) === String(conversationId));
                if (c) onSelectRef.current({ id: c.id, otherUser: c.otherUser });
            }
        };
        const openChannels = () => { setShowChannelsV2Modal(true); setV2UnreadCount(0); };
        const openProfile = (e) => { setDirectProfileCode(e.detail?.inviteCode || null); };
        window.addEventListener('open_chat_with_user', openChatWithUser);
        window.addEventListener('kaixo:open_chat', openChatById);
        window.addEventListener('open_channels_modal', openChannels);
        window.addEventListener('open_channels_v2_modal', openChannels);
        window.addEventListener('open_channel_profile', openProfile);
        return () => {
            window.removeEventListener('open_chat_with_user', openChatWithUser);
            window.removeEventListener('kaixo:open_chat', openChatById);
            window.removeEventListener('open_channels_modal', openChannels);
            window.removeEventListener('open_channels_v2_modal', openChannels);
            window.removeEventListener('open_channel_profile', openProfile);
        };
    }, []); // Registrar una sola vez — los refs mantienen estado fresco

    // Badge: contar anuncios V2 no leídos mientras el modal está cerrado.
    // Usa ref para que el handler lea el estado actual sin re-registrar el listener.
    const showV2Ref = useRef(showChannelsV2Modal);
    useEffect(() => { showV2Ref.current = showChannelsV2Modal; }, [showChannelsV2Modal]);

    useEffect(() => {
        const handler = () => {
            // Respetar preferencia de notificaciones de canales
            try { if (localStorage.getItem('kaixo_channel_notifs') === '0') return; } catch {}
            if (!showV2Ref.current) setV2UnreadCount(prev => prev + 1);
        };
        // Escuchar tanto V1 (channel_message) como V2 (channel_v2_message)
        const register = () => {
            const s = typeof getSocket === 'function' ? getSocket() : null;
            if (!s) return;
            s.off('channel_v2_message', handler);
            s.off('channel_message', handler);
            s.on('channel_v2_message', handler);
            s.on('channel_message', handler);
        };
        register();
        // Re-registrar cuando el socket se conecte (puede ser después del mount)
        window.addEventListener('socket_ready', register);
        // También re-registrar en cada reconexión
        const s0 = typeof getSocket === 'function' ? getSocket() : null;
        if (s0) s0.on('connect', register);
        return () => {
            window.removeEventListener('socket_ready', register);
            const s = typeof getSocket === 'function' ? getSocket() : null;
            if (s) {
                s.off('channel_v2_message', handler);
                s.off('channel_message', handler);
                s.off('connect', register);
            }
        };
    }, []);

    useEffect(() => {
        // Debounce 250ms: cuando llegan ráfagas de eventos (mensaje nuevo
        // → markRead → emit, todo en <100ms) hacemos UNA sola recarga en
        // vez de 3-5. Antes esto multiplicaba las llamadas a /conversations
        // y /groups por mensaje recibido.
        let debounceTimer = null;
        const debounced = () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => { debounceTimer = null; loadConvAndGroups(); }, 250);
        };
        window.addEventListener('conversations_updated', debounced);
        const handleNotification = (e) => {
            setNotification(e.detail);
            setTimeout(() => setNotification(null), 4000);
        };
        window.addEventListener('show_notification', handleNotification);
        return () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            window.removeEventListener('conversations_updated', debounced);
            window.removeEventListener('show_notification', handleNotification);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowHeaderMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // (la antigua lista combined conversations+groups ya no se usa: las
    // conversaciones se decoran sobre los contactos en PhoneContactsView y los
    // grupos se renderizan como sección independiente arriba.)

    if (loading) {
        return <div className="conversation-list">{t.conv.loading}</div>;
    }

    return (
        <div className="conversation-list">
            <div className="conversation-header">
                <div className="header-top-row">
                    <div className="app-brand app-brand-large">
                        <img
                            src={kaixoLogo}
                            alt="KAIXO"
                            className="app-brand-logo app-brand-logo-large"
                        />
                        <span className="app-brand-name app-brand-name-large">KAIXO</span>
                    </div>

                    <div className="header-menu-wrapper" ref={menuRef}>
                        <button className="new-chat-btn" onClick={() => setShowHeaderMenu(!showHeaderMenu)} aria-label="menu">
                            <Icons.More size={20} color="white" />
                        </button>
                    {showHeaderMenu && (
                        <div className="header-list-menu">
                            <div className="header-list-menu-item" onClick={() => { setShowProfileModal(true); setShowHeaderMenu(false); }}>
                                <Icons.User size={16} color="var(--text-secondary)" /> Editar mi perfil
                            </div>
                            <div className="header-list-menu-separator" />
                            <div className="header-list-menu-item" onClick={() => { setShowKaixoDirect(true); setShowHeaderMenu(false); }}>
                                <Icons.Globe size={16} color="var(--text-secondary)" /> Direct
                            </div>
                            <div className="header-list-menu-separator" />
                            <div className="header-list-menu-item" onClick={() => { setShowTranslateCamera(true); setShowHeaderMenu(false); }}>
                                <Icons.ScanText size={16} color="var(--text-secondary)" /> {t.chat.photoTranslate.attachLabel}
                            </div>
                            <div className="header-list-menu-separator" />
                            <div className="header-list-menu-item" onClick={() => { setSearchOpen(true); setSearchQuery(''); setSearchResults([]); setShowHeaderMenu(false); }}>
                                <Icons.Search size={16} color="var(--text-secondary)" /> {ui.common.search}
                            </div>
                            <div className="header-list-menu-separator" />
                            <div className="header-list-menu-item" onClick={() => { setShowCreateGroupModal(true); setShowHeaderMenu(false); }}>
                                <Icons.Group size={18} color="var(--text-secondary)" /> {ui.menu.createGroup}
                            </div>
                            <div className="header-list-menu-separator" />
                            <div className="header-list-menu-item" onClick={() => { setShowContactExchange(true); setShowHeaderMenu(false); }}>
                                <Icons.Share size={16} color="var(--text-secondary)" /> {ui.menu?.exchangeContact || 'Intercambiar contacto'}
                            </div>
                            <div className="header-list-menu-separator" />
                            <div className="header-list-menu-item" onClick={() => { setShowSettingsModal(true); setShowHeaderMenu(false); }}>
                                <Icons.Settings size={16} color="var(--text-secondary)" /> {ui.common.settings || 'Ajustes'}
                            </div>
                            <div className="header-list-menu-separator" />
                            <div className="header-list-menu-item" onClick={() => { setShowReactionGame(true); setShowHeaderMenu(false); }}>
                                <Icons.Gamepad size={16} color="var(--text-secondary)" /> Reto 1s
                            </div>
                            {currentUser?.isAdmin && (
                                <div
                                    className="header-list-menu-item"
                                    onClick={() => { setShowAdminSupport(true); setShowHeaderMenu(false); }}
                                >
                                    🛡️ Panel de Soporte
                                </div>
                            )}
                        </div>
                    )}
                    {showAdminSupport && (
                        <AdminPanel
                            currentUser={currentUser}
                            onClose={() => setShowAdminSupport(false)}
                        />
                    )}
                    {showKaixoDirect && (
                        <KaixoDirect onClose={() => setShowKaixoDirect(false)} currentUser={currentUser} />
                    )}
                    {showTranslateCamera && (
                        <TranslateCameraModal
                            userLanguage={currentUser?.language}
                            onClose={() => setShowTranslateCamera(false)}
                        />
                    )}
                    {showReactionGame && (
                        <ReactionGame onClose={() => setShowReactionGame(false)} />
                    )}
                </div>
                </div>

                <div className="main-tabs">
                    <button className={`main-tab ${mainTab === 'chats' ? 'active' : ''}`} onClick={() => setMainTab('chats')}>
                        <Icons.Send size={14} /> Chats
                    </button>
                    <button className={`main-tab ${mainTab === 'latidos' ? 'active' : ''}`} onClick={() => { setMainTab('latidos'); setV2UnreadCount(0); setLatidosOpenedOnce(true); }}>
                        <Icons.Pulse size={14} /> {ui.common.heartbeats || ui.menu?.heartbeats || 'Latidos'}
                        {v2UnreadCount > 0 && <span className="tab-badge">{v2UnreadCount > 9 ? '9+' : v2UnreadCount}</span>}
                    </button>
                    {hasImpulsaAccess(currentUser) && (
                        <button className={`main-tab crm-tab ${mainTab === 'crm' ? 'active' : ''}`} onClick={() => setMainTab('crm')}>
                            <Icons.Store size={14} /> {impulsaCt.title}
                        </button>
                    )}
                </div>
            </div>

            {notification && (
                <div className="group-notification">
                    <Icons.Group size={16} color="white" /> {notification.message}
                </div>
            )}

            {contactToast && (
                <div className="contact-toast" role="status">{contactToast}</div>
            )}

            {/* En web (embeddedDisabled=true), App.js renderiza Latidos / CRM
                en la columna2. Aquí solo los renderizamos en móvil para no
                tapar la lista de chats al cambiar de tab. */}
            {/* Render perezoso: solo desde la primera apertura. A partir
                de ahí mantenemos el modal montado y solo lo OCULTAMOS al
                cambiar de pestaña. Así v2GetFeed + v2GetUserChannels solo
                se llaman una vez por sesión y el cambio de pestaña es
                instantáneo (los socket listeners siguen actualizando el
                feed en background mientras está oculto). */}
            {!embeddedDisabled && latidosOpenedOnce && (
                <div style={{
                    display: mainTab === 'latidos' ? 'flex' : 'none',
                    flex: 1,
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                    /* isolation:isolate crea un stacking context propio →
                       evita que el GPU compositor del WebView Huawei mezcle
                       buffers entre layers cuando se cambia display:none/flex
                       al alternar pestañas (eso causaba las "líneas detrás
                       del feed" reportadas). */
                    isolation: 'isolate'
                }}>
                    <ChannelsV2Modal currentUser={currentUser} onClose={() => setMainTab('chats')} embedded={true} />
                </div>
            )}

            {!embeddedDisabled && mainTab === 'crm' && hasImpulsaAccess(currentUser) && (
                <CRMModal currentUser={currentUser} onClose={() => setMainTab('chats')} embedded={true} />
            )}

            {/* Sidebar de notas del CRM cuando estamos en web + Impulsa.
                Reemplaza la lista de chats (no el header con tabs), así el
                usuario puede volver a Chats / Latidos pulsando los tabs. */}
            {showCrmSidebarInWeb && mainTab === 'crm' && (
                <CRMActivitySidebar />
            )}

            {/* Pestaña Chats unificada: en lugar de listar solo las conversaciones
                existentes, mostramos la agenda completa del usuario (con KAIXO arriba
                + invitar abajo) y decoramos cada contacto con KAIXO con su último
                mensaje y hora si tiene conversación abierta. Los grupos van encima.
                En web cuando está activo el sidebar de Impulsa, ocultamos esta lista. */}
            <div className="conversations" style={{ display: (mainTab === 'chats' && !(showCrmSidebarInWeb && mainTab === 'crm')) ? 'block' : 'none' }}>
                {/* Lista unificada de chats: Support + grupos + contactos
                    KAIXO + invitar — todo mezclado, sin secciones separadas.
                    PhoneContactsView se encarga de fusionar grupos como si
                    fueran un contacto más. */}
                <PhoneContactsView
                    currentUser={currentUser}
                    onSelectConversation={onSelectConversation}
                    onlineUsers={onlineUsers}
                    conversations={conversations}
                    groups={groups}
                    onAddDeviceContact={handleAddDeviceContact}
                />
            </div>

            {showCreateGroupModal && (
                <CreateGroupModal
                    currentUser={currentUser}
                    onClose={() => setShowCreateGroupModal(false)}
                    onGroupCreated={() => {
                        setShowCreateGroupModal(false);
                        loadAll();
                    }}
                />
            )}


            {showChannelsV2Modal && (
                <ChannelsV2Modal
                    currentUser={currentUser}
                    onClose={() => setShowChannelsV2Modal(false)}
                />
            )}

            {showContactExchange && (
                <ContactExchangeModal onClose={() => setShowContactExchange(false)} />
            )}
            {showSettingsModal && (
                <SettingsModal onClose={() => setShowSettingsModal(false)} />
            )}

            {directProfileCode && (
                <ChannelProfile
                    inviteCode={directProfileCode}
                    currentUser={currentUser}
                    onClose={() => setDirectProfileCode(null)}
                    onSubscribed={() => {
                        setDirectProfileCode(null);
                        setShowChannelsV2Modal(true);
                        setV2UnreadCount(0);
                        setTimeout(() => window.dispatchEvent(new Event('v2_channels_updated')), 300);
                    }}
                />
            )}

            {showProfileModal && (
                <ProfileModal
                    currentUser={currentUser}
                    onClose={() => setShowProfileModal(false)}
                    onUpdate={(updatedUser) => {
                        window.dispatchEvent(new CustomEvent('user_updated', { detail: updatedUser }));
                        setShowProfileModal(false);
                    }}
                />
            )}

            {searchOpen && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '8vh'
                }} onClick={() => setSearchOpen(false)}>
                    <div style={{
                        background: 'white', borderRadius: '14px', width: '92%', maxWidth: '520px',
                        maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: '1px solid #eee' }}>
                            <Icons.Search size={18} color="#888" />
                            <input
                                type="text"
                                autoFocus
                                placeholder="Buscar en todos los chats..."
                                value={searchQuery}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                style={{ flex: 1, border: 'none', outline: 'none', fontSize: '15px', fontFamily: 'Nunito, sans-serif' }}
                            />
                            <button onClick={() => setSearchOpen(false)} aria-label={ui.common.close} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>
                                <Icons.Close size={18} />
                            </button>
                        </div>
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                            {searching && <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Buscando...</div>}
                            {!searching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                                <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Sin resultados</div>
                            )}
                            {!searching && searchResults.map(r => {
                                const conv = r.conversationId ? conversations.find(c => c.id === r.conversationId.toString()) : null;
                                const chatLabel = r.chatName || conv?.otherUser?.name || 'Chat';
                                return (
                                    <div key={r.id} onClick={() => handleResultClick(r)} style={{
                                        padding: '12px 16px', borderBottom: '1px solid #f3f3f3', cursor: 'pointer',
                                        display: 'flex', flexDirection: 'column', gap: '4px'
                                    }} onMouseOver={e => e.currentTarget.style.background = '#fafafa'} onMouseOut={e => e.currentTarget.style.background = 'white'}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <strong style={{ fontSize: '13px', color: '#F5A000' }}>{chatLabel}</strong>
                                            <span style={{ fontSize: '11px', color: '#999' }}>
                                                {new Date(r.timestamp).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#666' }}>
                                            <span style={{ fontWeight: 600 }}>{r.sender.name}: </span>
                                            <span>{r.text}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
            <ImageViewer src={fullImage} onClose={() => setFullImage(null)} />
        </div>
    );
}

export default ConversationList;